import { computed } from "vue";
import { useStore } from "vuex";
import { type DownloadFormatType } from "hoxton-shared/enums/exports";
import { TemplateType } from "hoxton-shared/enums/masterTemplate";

import { useDownloadHTMLVideo } from "@/composables/download/useDownloadHTMLVideo";
import { useDownloadHTMLZip } from "@/composables/download/useDownloadHTMLZip";
import { useDownloadFlashtalkingExcel } from "@/composables/download/useDownloadFlashtalkingExcel";
import { useDownloadAEVideo } from "@/composables/download/useDownloadAEVideo";
import type { RootState } from "@/store/types";

export function useDownload() {
    const store = useStore<RootState>();
    const campaignType = computed(() => store.state.campaign.campaignType ?? TemplateType.HTML5);

    const { computeQueryParams: computeQueryParamsVideo, resetDownloadOptions: resetVideoDownloadOptions } =
        useDownloadHTMLVideo();

    const {
        computeQueryParams: computeQueryParamsHTMLZip,
        resetDownloadOptions: resetDownloadOptionsHTMLZip,
        isButtonDisabled: isButtonDisabledHTMLZip
    } = useDownloadHTMLZip();

    const {
        computeQueryParams: computeFlashTalkingExcelParams,
        resetDownloadOptions: resetFlashTalkingExcelOptions,
        isButtonDisabled: isButtonDisabledFlashTalkingExcel
    } = useDownloadFlashtalkingExcel();

    const { isButtonDisabled: isButtonDisabledAEVideo, resetValues: resetAEVideoValues } = useDownloadAEVideo();

    function resetDownloadOptions() {
        resetDownloadOptionsHTMLZip();
        resetFlashTalkingExcelOptions();
        resetVideoDownloadOptions();
        resetAEVideoValues();
    }

    function computeQueryParams(downloadFormat: DownloadFormatType) {
        return {
            ...computeQueryParamsHTMLZip(downloadFormat),
            ...computeFlashTalkingExcelParams(downloadFormat),
            ...computeQueryParamsVideo(downloadFormat)
        };
    }

    function getIsButtonDisabled(platform: DownloadFormatType | "") {
        if (platform === "") {
            return true;
        }

        if (campaignType.value === TemplateType.AE) {
            return isButtonDisabledAEVideo.value;
        }

        if (campaignType.value === TemplateType.HTML5) {
            return (
                isButtonDisabledHTMLZip.value[platform] ?? isButtonDisabledFlashTalkingExcel.value[platform] ?? false
            );
        }

        return false;
    }

    return {
        getIsButtonDisabled,
        resetDownloadOptions,
        computeQueryParams
    };
}
